import { create } from "zustand";
import axios from "axios";
import { formatNameForUrl } from "@/lib/getCardLink";
import { getVersion } from "@/lib/version";
import { CDN } from "@/lib/cdn";

export interface Series {
  id: string;
  name: string;
  region: string;
  setName: string;
  releaseDate: string;
}

export interface FormatReleaseDate {
  original: string;
  name: string;
  set: string;
  category: string;
  release_date: string;
}

interface SeriesState {
  // Series data
  series: Series[];
  seriesLoading: boolean;
  seriesError: string | null;

  // Format release dates
  formatReleaseDates: Record<string, FormatReleaseDate[]>;
  formatReleaseDatesLoading: boolean;
  formatReleaseDatesError: string | null;

  // Actions
  fetchSeries: () => Promise<void>;
  fetchFormatReleaseDates: () => Promise<void>;

  // Helper functions
  getFormatName: (groupId: string) => string;
  getGroupLink: (seriesName: string) => string;
}

function getDateVersion(): string {
  const currentDate = new Date();
  return `6.${currentDate.getMonth() + 1}.${currentDate.getDate()}.${currentDate.getFullYear()}`;
}

const cache: { [key: string]: any } = {};

async function fetchData(url: string): Promise<any> {
  if (cache[url]) {
    return cache[url];
  }

  try {
    const response = await axios.get(url);
    cache[url] = response.data;
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}:`, error);
    throw new Error(`Could not fetch data from ${url}`);
  }
}

export const useSeriesStore = create<SeriesState>((set, get) => ({
  // Initial state
  series: [],
  seriesLoading: false,
  seriesError: null,

  formatReleaseDates: {},
  formatReleaseDatesLoading: false,
  formatReleaseDatesError: null,

  // Fetch series data
  fetchSeries: async () => {
    // Only fetch on client side
    if (typeof window === "undefined") {
      return;
    }

    const state = get();

    // Don't fetch if already loaded or currently loading
    if (state.series.length > 0 || state.seriesLoading) {
      return;
    }

    set({ seriesLoading: true, seriesError: null });

    try {
      // Use API route to avoid CORS issues
      const url = `/api/series`;
      const data = await fetchData(url);

      set({ series: data, seriesLoading: false });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      set({ seriesError: errorMessage, seriesLoading: false });
    }
  },

  // Fetch format release dates
  fetchFormatReleaseDates: async () => {
    // Only fetch on client side
    if (typeof window === "undefined") {
      return;
    }

    const state = get();

    // Don't fetch if already loaded or currently loading
    if (
      Object.keys(state.formatReleaseDates).length > 0 ||
      state.formatReleaseDatesLoading
    ) {
      return;
    }

    set({ formatReleaseDatesLoading: true, formatReleaseDatesError: null });

    try {
      const version = getDateVersion();
      const asiaUrl = `${CDN}/games/one-piece/data/ASIA-EN/release_dates.json?v=${version}`;
      const enUrl = `${CDN}/games/one-piece/data/EN/release_dates.json?v=${version}`;
      const globalUrl = `${CDN}/games/one-piece/data/GLOBAL/release_dates.json?v=${version}`;

      // Fetch ASIA-EN and EN, but try GLOBAL and don't fail if it doesn't exist
      const [asiaData, enData] = await Promise.all([
        fetchData(asiaUrl),
        fetchData(enUrl),
      ]);

      // Try to fetch GLOBAL data, but don't fail if it doesn't exist
      let globalData = null;
      try {
        globalData = await fetchData(globalUrl);
      } catch (err) {
        // GLOBAL data is optional, so we don't need to log an error
      }

      const releaseDates: Record<string, FormatReleaseDate[]> = {
        "ASIA-EN": asiaData,
        EN: enData,
      };

      if (globalData) {
        releaseDates.GLOBAL = globalData;
      }

      set({
        formatReleaseDates: releaseDates,
        formatReleaseDatesLoading: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      set({
        formatReleaseDatesError: errorMessage,
        formatReleaseDatesLoading: false,
      });
    }
  },

  // Helper functions
  getFormatName: (groupId: string) => {
    const state = get();

    // Auto-fetch if not loaded yet
    if (state.series.length === 0 && !state.seriesLoading) {
      state.fetchSeries();
    }

    for (const s of state.series) {
      if (s.name === groupId) {
        return s.setName;
      }
    }
    return "";
  },

  getGroupLink: (seriesName: string) => {
    const state = get();
    const formatName = state.getFormatName(seriesName);
    return `/cards/${seriesName}/${formatNameForUrl(formatName)}`;
  },
}));
